/**
 * PR-BT-09B — Web "Mobile Action Sync" panel.
 *
 * Shows sync status, pending mobile action requests, action details,
 * confirm/deny buttons, results, expiry, and Emergency Stop state.
 * Owner-only — mirrors the desktop panel with same labels and rules.
 */
import { useEffect, useState, useCallback } from "react";
import { api, type MobileActionRequestRow, type MobileActionResultRow } from "../api.js";
import { useLocale } from "../ui.js";

const STATUS_COLORS: Record<string, string> = {
  pending_confirmation: "var(--tertiary)",
  confirmed: "var(--ready)",
  denied: "var(--blocked)",
  executing: "var(--tertiary)",
  completed: "var(--ready)",
  expired: "var(--text-muted)",
  failed: "var(--blocked)",
};

const ALLOWLIST_LABELS: Record<string, [string, string]> = {
  get_status: ["Get Status", "स्थिति देखें"],
  request_emergency_stop: ["Request Emergency Stop", "इमरजेंसी स्टॉप अनुरोध"],
  get_emergency_stop_status: ["Get Emergency Stop Status", "इमरजेंसी स्टॉप स्थिति"],
  list_pending_approvals: ["List Pending Approvals", "लंबित मंज़ूरियाँ"],
  request_readonly_action: ["Request Read-only Action", "रीड-ओनली action अनुरोध"],
  get_action_result: ["Get Action Result", "action परिणाम देखें"],
};

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt).getTime() <= Date.now();
}

export function MobileActionSyncPanel({ token }: { token: string }) {
  const { tr } = useLocale();
  const [pending, setPending] = useState<MobileActionRequestRow[]>([]);
  const [emergencyStop, setEmergencyStop] = useState(false);
  const [activeDevices, setActiveDevices] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; status: number; data: { ok: boolean; status: string; result: MobileActionResultRow | null } | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const refresh = useCallback(async () => {
    const statusRes = await api.mobilePairingStatus(token);
    if (statusRes.status === 403) { setDenied(true); return; }
    if (statusRes.ok && statusRes.data) {
      setEmergencyStop(statusRes.data.emergencyStop);
      setActiveDevices(statusRes.data.activeDevices);
    }
    const pendingRes = await api.mobilePairingPending(token);
    if (pendingRes.ok && pendingRes.data) {
      setPending(pendingRes.data.actions);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 5000);
    return () => clearInterval(id);
  }, [refresh]);

  const viewResult = async (reqId: string): Promise<void> => {
    setBusy(true);
    const r = await api.mobilePairingResult(token, reqId);
    setResult(r);
    setBusy(false);
  };

  const doConfirm = async (reqId: string): Promise<void> => {
    setBusy(true);
    await api.mobilePairingConfirm(token, reqId, true);
    await refresh();
    setBusy(false);
  };

  const doDeny = async (reqId: string): Promise<void> => {
    setBusy(true);
    await api.mobilePairingConfirm(token, reqId, false);
    await refresh();
    setBusy(false);
  };

  if (denied) {
    return (
      <section className="panel-card">
        <h2>{tr("jarvisCenter")}</h2>
        <div className="msg info">{tr("noAccess")}</div>
      </section>
    );
  }

  return (
    <section className="panel-card">
      <h3>Mobile Action Sync</h3>
      <p className="section-desc">
        Pending mobile action requests from paired devices. Owner confirmation required for every action.
        Emergency Stop blocks all except status reads.
      </p>

      {/* Status summary */}
      <div className="jarvis-links" style={{ marginBottom: 12 }}>
        <span className={`badge ${emergencyStop ? "error" : "ok"}`}>
          Emergency Stop: {emergencyStop ? "ACTIVE" : "idle"}
        </span>
        <span className="badge">Devices: {activeDevices}</span>
        <span className="badge">Pending: {pending.length}</span>
      </div>

      {emergencyStop && (
        <div className="msg error" style={{ marginBottom: 10 }}>
          EMERGENCY STOP ACTIVE — all mobile actions blocked except status reads.
        </div>
      )}

      {/* Pending actions list */}
      {pending.length === 0 ? (
        <p className="muted">No pending actions.</p>
      ) : (
        <div>
          {pending.map((req) => {
            const expired = isExpired(req.expiresAt);
            const isSelected = selectedId === req.requestId;
            const label = ALLOWLIST_LABELS[req.actionId];
            return (
              <div
                key={req.requestId}
                className="jarvis-help"
                style={{
                  marginBottom: 8,
                  cursor: "pointer",
                  border: isSelected ? "2px solid var(--tertiary)" : "1px solid var(--border)",
                  borderRadius: 10,
                  padding: 12,
                  opacity: expired ? 0.5 : 1,
                }}
                onClick={() => { setSelectedId(req.requestId); void viewResult(req.requestId); }}
              >
                <div className="row">
                  <strong>{label ? label[0] : req.actionId}</strong>
                  <span style={{ color: STATUS_COLORS[req.status] ?? "var(--text-muted)", fontSize: 12, fontFamily: "monospace" }}>
                    {req.status}
                  </span>
                </div>
                <div className="row" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  <span style={{ fontFamily: "monospace" }}>{req.deviceId.slice(0, 12)}…</span>
                  <span>{expired ? "expired" : req.targetScope}</span>
                </div>

                {isSelected && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>Request ID:</strong> <span style={{ fontFamily: "monospace" }}>{req.requestId}</span>
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>Created:</strong> {req.createdAt}
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>Expires:</strong> {req.expiresAt}
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>Payload:</strong> <span style={{ fontFamily: "monospace" }}>{JSON.stringify(req.payload)}</span>
                    </div>

                    {/* Result */}
                    {result && result.data && (
                      <div style={{ marginTop: 8, padding: 8, border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}>
                        <div className="row">
                          <span>Result status</span>
                          <strong style={{ color: STATUS_COLORS[result.data.status] ?? "var(--text-muted)", fontFamily: "monospace" }}>
                            {result.data.status}
                          </strong>
                        </div>
                        {result.data.result && (
                          <>
                            <div className="row">
                              <span>Success</span>
                              <strong style={{ fontFamily: "monospace" }}>{String(result.data.result.success)}</strong>
                            </div>
                            <div className="row">
                              <span>Output</span>
                              <span style={{ fontFamily: "monospace" }}>{result.data.result.sanitizedOutput}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Confirm / Deny */}
                    {req.status === "pending_confirmation" && !expired && (
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <button
                          type="button"
                          className="ss-login-submit"
                          disabled={busy || emergencyStop}
                          onClick={(e) => { e.stopPropagation(); void doConfirm(req.requestId); }}
                          style={{ background: "var(--ready)" }}
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          className="ss-login-submit"
                          disabled={busy}
                          onClick={(e) => { e.stopPropagation(); void doDeny(req.requestId); }}
                          style={{ background: "var(--blocked)" }}
                        >
                          Deny
                        </button>
                      </div>
                    )}
                    {expired && (
                      <div style={{ marginTop: 8, padding: 8, color: "var(--text-muted)", border: "1px solid var(--text-muted)", borderRadius: 8 }}>
                        Expired — will not execute on reconnect.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="jarvis-links" style={{ marginTop: 12 }}>
        <span className="muted" style={{ fontSize: 12 }}>tokenPersisted:false · autoExecute:false · expiredDiscarded:true</span>
      </div>
    </section>
  );
}
